import TextInput from "components/TextInput";
import { useOutsideClick } from "hooks";
import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import styled from "styled-components";
import { colors } from "theme/colors";

interface SuggestiveTextInputProps {
    label: string;
    name?: string;
    placeholder: string;
    value: string;
    options: { value: string; label: string }[];
    errorText?: string;
    onChange: (value: string) => void;
    minWidth?: string;
    required?: boolean;
    disabled?: boolean;
}

const Wrapper = styled.div<{ minWidth?: string }>`
    position: relative;
    min-width: ${(props) => props.minWidth || "10rem"};
`;

const SuggestionsList = styled.ul`
    position: absolute;
    z-index: 4;
    width: 100%;
    max-height: 200px;
    overflow: auto;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: rgb(176 188 200) 0px 2px 5px;
    font-family:Montserrat, sans-serif
    font-size:10px;
    font-weight: 600
`;

const SuggestionItem = styled.li`
    padding: 0.5rem;
    cursor: pointer;

    &:hover {
        background-color: #f1f1f1;
    }
`;

const ErrorText = styled.span`
    color: red;
    font-size: 0.875rem;
`;

const SuggestiveTextInput: FC<SuggestiveTextInputProps> = ({
    label,
    placeholder,
    value,
    name = "",
    options,
    errorText,
    onChange,
    minWidth,
    required = false,
    disabled = false,
}) => {
    const [areOptionsVisible, setOptionsVisibilityState] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const showOptions = useCallback(() => {
        inputRef.current?.focus();
        setOptionsVisibilityState(true);
        setFilteredOptions(options);
    }, [options]);

    const hideOptions = useCallback(() => {
        inputRef.current?.blur();
        setOptionsVisibilityState(false);
    }, []);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setInputValue(inputValue);

        const newOptions = options.filter((option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
        );

        setFilteredOptions(newOptions);
        if (newOptions.length > 0) {
            setOptionsVisibilityState(true);
        } else {
            setOptionsVisibilityState(false);
        }
        onChange(inputValue);
    };

    const handleSuggestionClick = (suggestion: string) => {
        setInputValue(suggestion);
        onChange(suggestion);
        hideOptions();
    };

    useOutsideClick(wrapperRef, () => {
        if (areOptionsVisible) {
            hideOptions();
        }
    });

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <Wrapper ref={wrapperRef} minWidth={minWidth}>
            <TextInput
                label={label}
                name={name}
                type="text"
                autoComplete="off"
                required={required}
                ref={inputRef}
                placeholder={placeholder}
                value={inputValue}
                disabled={disabled}
                onChange={handleInputChange}
                onFocus={showOptions}
                suffix={
                    areOptionsVisible ? (
                        <BsChevronUp
                            size={12}
                            color={colors.purple.primary}
                            onClick={hideOptions}
                        />
                    ) : (
                        <BsChevronDown
                            size={12}
                            color={colors.purple.primary}
                            onClick={showOptions}
                        />
                    )
                }
                errorMsg={errorText}
            />
            {areOptionsVisible && filteredOptions.length > 0 && (
                <SuggestionsList>
                    {filteredOptions.map((option) => (
                        <SuggestionItem
                            key={option.value}
                            onClick={() => handleSuggestionClick(option.label)}
                        >
                            {option.label}
                        </SuggestionItem>
                    ))}
                </SuggestionsList>
            )}
            {/* {errorText && <ErrorText>{errorText}</ErrorText>} */}
        </Wrapper>
    );
};

export default SuggestiveTextInput;
